import { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useTrack, useDeleteTrack } from "@/hooks/useTracks";
import { TrackModal } from "@/components/tracks/TrackModal";
import { TrackMap } from "@/components/maps";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { atLeastAccess } from "@/utils/accessLevel";
import { formatLapTime } from "@/utils/timeFormat";

/**
 * Dedicated track detail page at /tracks/:trackId. Replaced the former
 * TrackModal view mode. Track mutations are admin-only on the backend
 * (requireAccessLevel(AccessLevel.admin) in backend/src/routes/tracks.ts),
 * so Edit/Delete render only for admins via atLeastAccess.
 */
export default function TrackDetailPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { trackId = "" } = useParams<{ trackId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: track, isLoading, error } = useTrack(trackId);
  const deleteTrackMutation = useDeleteTrack();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  usePageMeta({ title: track?.name, description: track?.description });

  const isAdmin = atLeastAccess(user?.accessLevel, "admin");

  const handleDelete = async () => {
    try {
      await deleteTrackMutation.mutateAsync(trackId);
      setIsDeleteConfirmOpen(false);
      navigate("/tracks");
    } catch (err: unknown) {
      let message = t("pages:tracks.deleteError");

      if (axios.isAxiosError(err)) {
        message =
          (err.response?.data as { error?: { message?: string } })?.error
            ?.message || message;
      } else if (err instanceof Error) {
        message = err.message;
      }

      setDeleteError(message);
    }
  };

  const backLink = (
    <Link
      to="/tracks"
      className="inline-flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-600 transition-colors"
    >
      <ArrowLeft className="h-4 w-4" />
      {t("pages:tracks.detail.backToTracks")}
    </Link>
  );

  if (error || !trackId || (!isLoading && !track)) {
    const message = error
      ? t("pages:tracks.detail.errorLoading")
      : t("pages:tracks.detail.notFound");

    return (
      <div className="page-container">
        <div className="mb-8">{backLink}</div>
        <div className="bg-white rounded-lg border border-secondary-200 p-8 text-center">
          <MapPin className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
          <p className="text-lg text-red-600 mb-4">{message}</p>
          <button
            onClick={() => navigate("/tracks")}
            className="btn-primary px-4 py-2"
          >
            {t("pages:tracks.detail.backToTracks")}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="animate-pulse space-y-6">
          <div className="h-4 bg-secondary-200 rounded w-32"></div>
          <div className="h-8 bg-secondary-200 rounded w-1/3"></div>
          <div className="h-64 bg-secondary-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!track) return null;

  return (
    <div className="page-container">
      <div className="mb-8">
        {backLink}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mt-3">
          <div>
            <h1 className="page-title">{track.name}</h1>
            <p className="page-subtitle">
              {t(`pages:tracks.form.typeOptions.${track.type}`)}
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="btn-secondary px-4 py-2"
              >
                {t("pages:tracks.detail.edit")}
              </button>
              <button
                onClick={() => {
                  setDeleteError(null);
                  setIsDeleteConfirmOpen(true);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t("pages:tracks.delete")}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-secondary-200 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <h3 className="section-title mb-3">
              {t("pages:tracks.modal.trackInformation")}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-secondary-600">
                  {t("pages:tracks.modal.typeLabel")}
                </span>
                <span className="font-medium">
                  {t(`pages:tracks.form.typeOptions.${track.type}`)}
                </span>
              </div>
              {track.length && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.lengthLabel")}
                  </span>
                  <span className="font-medium">{track.length.toFixed(2)} km</span>
                </div>
              )}
              {track.numberOfTurns && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.turnsLabel")}
                  </span>
                  <span className="font-medium">{track.numberOfTurns}</span>
                </div>
              )}
              {track.elevation && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.elevationLabel")}
                  </span>
                  <span className="font-medium">
                    {track.elevation.toFixed(1)} m
                  </span>
                </div>
              )}
              {track.yearOpened && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.openedLabel")}
                  </span>
                  <span className="font-medium">{track.yearOpened}</span>
                </div>
              )}
              {track.lapRecord && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.lapRecordLabel")}
                  </span>
                  <span className="font-medium">
                    {formatLapTime(track.lapRecord)}
                  </span>
                </div>
              )}
              {track.lapRecordHolder && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.recordHolderLabel")}
                  </span>
                  <span className="font-medium">{track.lapRecordHolder}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-secondary-600">
                  {t("pages:tracks.modal.coordinatesLabel")}
                </span>
                <span className="font-mono text-xs">
                  {track.latitude.toFixed(6)}, {track.longitude.toFixed(6)}
                </span>
              </div>
              {track.createdAt && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.addedLabel")}
                  </span>
                  <span>
                    {new Date(track.createdAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              {track.updatedAt && track.updatedAt !== track.createdAt && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    {t("pages:tracks.modal.updatedLabel")}
                  </span>
                  <span>
                    {new Date(track.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="section-title mb-3">
              {t("pages:tracks.modal.mapLocation")}
            </h3>
            <TrackMap
              latitude={track.latitude}
              longitude={track.longitude}
              trackName={track.name}
              zoom={15}
              className="w-full h-64 lg:h-80 rounded-lg border border-secondary-200"
            />
          </div>
        </div>

        {track.description && (
          <div className="mt-6">
            <h3 className="section-title mb-2">{t("common:description")}</h3>
            <p className="text-secondary-800 whitespace-pre-wrap">
              {track.description}
            </p>
          </div>
        )}
      </div>

      <TrackModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        track={track}
        mode="edit"
      />

      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t("pages:tracks.deleteTitle")}
        message={t("pages:tracks.deleteConfirmation", { name: track.name })}
        confirmButtonText={t("pages:tracks.delete")}
        isLoading={deleteTrackMutation.isPending}
      />

      {deleteError && (
        <div
          role="alert"
          className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-3 rounded shadow-lg z-50 flex items-start gap-3 max-w-sm"
        >
          <span>{deleteError}</span>
          <button
            className="ml-auto text-white/80 hover:text-white"
            aria-label={t("common:close")}
            onClick={() => setDeleteError(null)}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
